// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  hooks/useExportacao.ts
//  Hook para exportar relatórios em Excel/CSV via download binário.
// ════════════════════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import { apiClient } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';

export type FormatoExportacao = 'xlsx' | 'csv';

interface UseExportacaoOptions {
  /** URL base do endpoint de exportação (ex: /api/relatorios/vendas/clientes/exportar) */
  endpoint: string;
  /** Parâmetros de query (filtros) que serão enviados junto */
  params?: Record<string, string | number | boolean | undefined | null>;
}

interface UseExportacaoReturn {
  exportar: (formato?: FormatoExportacao) => Promise<void>;
  isExporting: boolean;
}

/**
 * Hook genérico para exportação de relatórios.
 * Faz download do arquivo binário (XLSX/CSV) retornado pela API.
 */
export function useExportacao({ endpoint, params }: UseExportacaoOptions): UseExportacaoReturn {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const exportar = useCallback(async (formato: FormatoExportacao = 'xlsx') => {
    setIsExporting(true);
    try {
      // Monta query params, removendo nulos/undefined/vazios
      const queryParams: Record<string, string> = { formato };
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') {
            queryParams[k] = String(v);
          }
        });
      }

      const queryString = new URLSearchParams(queryParams).toString();
      const url = `${endpoint}?${queryString}`;

      const response = await apiClient.get(url, {
        responseType: 'blob',
        timeout: 60_000, // 60s para exportações grandes
      });

      // Extrai o nome do arquivo do header Content-Disposition ou gera fallback
      const contentDisposition = response.headers['content-disposition'] as string | undefined;
      let filename = `relatorio.${formato}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[*]?=(?:UTF-8'')?["']?([^"';\n]+)/i);
        if (match?.[1]) {
          filename = decodeURIComponent(match[1]);
        }
      }

      // Cria blob e dispara download
      const blob = new Blob([response.data], {
        type: formato === 'csv'
          ? 'text/csv; charset=utf-8'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      toast({
        title: 'Exportação concluída',
        description: `Arquivo ${filename} baixado com sucesso.`,
      });
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Erro ao exportar relatório.';
      toast({
        title: 'Erro na exportação',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  }, [endpoint, params, toast]);

  return { exportar, isExporting };
}
